import type { CustomFieldType } from '@/lib/custom-fields/types/custom-field-type';
import { BASE_BACKEND_URL, CLIENT_TYPE } from '@/requests/constants';
import { useQuery } from '@tanstack/react-query';

export interface ComponentConditionValue {
  id: string;
  name: string;
  order: number;
  color_code: string;
}

export interface DefaultDescription {
  id: string;
  code: string;
  title: string;
  description: string;
}

export interface DefaultAction {
  id: string;
  code: string;
  title: string;
  description: string;
  proposal: string;
}

export interface ErrandTypeContext {
  id: string;
  name: string;
  sub_type_configuration: number[];
  default_descriptions: string[];
  default_priority: number | null;
  allowed_in_portals: number[];
  description_configuration: number[];
  images_configuration: number[];
  film_recordings_configuration: number[];
  files_configuration: number[];
  address_configuration: number[];
  object_access_configuration: number[];
  object_access_available_options: number[];
  has_pets_configuration: number[];
  priority_configuration: number[];
  is_warrant_configuration: number[];
  temporary_contact_ssn_configuration: number[];
  temporary_contact_org_no_configuration: number[];
  temporary_contact_first_name_configuration: number[];
  temporary_contact_last_name_configuration: number[];
  temporary_contact_phone_number_configuration: number[];
  temporary_contact_email_configuration: number[];
  show_default_descriptions: boolean;
  general_information_view_configuration: number[];
  temporary_contact_view_configuration: number[];
  sensitive_personal_information_view_configuration: number[];
  actions_view_configuration: number[];
  reporter_view_configuration: number[];
  creator_view_configuration: number[];
  performer_view_configuration: number[];
  object_connection_view_configuration: number[];
}

export interface ErrandSubTypeContext extends ErrandTypeContext {
  errand_type: { id: string };
  overrides_default_descriptions: boolean;
}

export interface ContextCustomFieldType {
  id: string;
  title: string;
  description: string | null;
  kind: CustomFieldType;
  required: boolean;
  options: string[];
}

export interface ContextCustomFieldInclusion {
  id: string;
  order: number;
  general_errand_type: string | null;
  sub_errand_type: string | null;
  field_type: ContextCustomFieldType;
}

interface ErrandContextAddress {
  id: string;
  street: string;
  street_number: string;
  postal_code: string;
  city: string;
  country: string;
  longitude: number;
  latitude: number;
}

export interface ErrandContextResponse {
  errand_types: ErrandTypeContext[];
  errand_sub_types: ErrandSubTypeContext[];
  default_descriptions: DefaultDescription[];
  default_actions: DefaultAction[];
  addresses: ErrandContextAddress[];
  component_condition_values: ComponentConditionValue[];
  custom_field_inclusions?: ContextCustomFieldInclusion[];
}

export function useGetErrandContext({
  customerId,
  enabled = true,
}: {
  customerId: string;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ['errand-context', customerId],
    queryFn: async () => {
      const res = await fetch(
        `${BASE_BACKEND_URL}/errands/generalerrands/reporting/anonymous/context/`,
        {
          headers: {
            'X-PIGELLO-CUSTOMER-ID': customerId,
            'X-PIGELLO-CLIENT-TYPE': `${CLIENT_TYPE},pigelloanon,1.0.0`,
          },
        }
      );
      if (!res.ok) {
        throw new Error('Failed to fetch errand context');
      }
      const data: ErrandContextResponse = await res.json();
      return data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** Anonymous view configuration values */
const ANON_VIEW = 1;

/** Anonymous field configuration values */
const ANON_ALLOW = 4;
const ANON_REQUIRE = 5;

/**
 * Check if a section is visible for anonymous users based on view configuration.
 */
export function isVisibleForAnonymous(
  viewConfiguration: number[] | undefined | null
): boolean {
  return viewConfiguration?.includes(ANON_VIEW) ?? false;
}

/**
 * Check if a field is allowed for anonymous users based on field configuration.
 */
export function isAllowedForAnonymous(
  fieldConfiguration: number[] | undefined | null
): boolean {
  if (!fieldConfiguration) return false;
  return (
    fieldConfiguration.includes(ANON_ALLOW) ||
    fieldConfiguration.includes(ANON_REQUIRE)
  );
}

/**
 * Check if a field is required for anonymous users based on field configuration.
 */
export function isRequiredForAnonymous(
  fieldConfiguration: number[] | undefined | null
): boolean {
  return fieldConfiguration?.includes(ANON_REQUIRE) ?? false;
}

/**
 * Resolve the effective errand type context for a given errand.
 * If a sub-type exists, its config values override the parent type's.
 */
export function resolveErrandTypeConfig(
  contextData: ErrandContextResponse | undefined,
  errandTypeId: string | undefined | null,
  errandSubTypeId: string | undefined | null
): ErrandTypeContext | undefined {
  if (!contextData || !errandTypeId) return undefined;

  const errandType = contextData.errand_types.find(
    (t) => t.id === errandTypeId
  );
  if (!errandType) return undefined;

  if (!errandSubTypeId) return errandType;

  const errandSubType = contextData.errand_sub_types.find(
    (st) => st.id === errandSubTypeId
  );
  if (!errandSubType) return errandType;

  const merged = { ...errandType };
  for (const [key, value] of Object.entries(errandSubType)) {
    if (value != null) {
      (merged as Record<string, unknown>)[key] = value;
    }
  }
  return merged;
}
